import React, { useState } from "react";
import {
  BlueLabel,
  HR,
  VR,
  FlexColumn,
  InputLabel,
  RowContainer,
  Label,
  Value,
  WorkflowScheduleContainer,
  LabelContainer,
} from "./settings.styles";
import {
  BlackHighlight,
  StepContainer,
  BoxTitle,
} from "../createWorkflowViewer.styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  TextField,
  useValidation,
  Button,
  ScheduleInfo,
} from "@ssce/ui-shared-library";
import { ScheduleSidebar } from "../createWorkflowSidebars";
import { SettingsData } from "../createWorkflowViewerHook";

const validation = (workflowType) => [
  {
    name: [
      (value) => {
        if (!value) return "Name is required";
      },
    ],
    project: [
      (value) => {
        if (!value) return "Project is required";
      },
    ],
    dataMeshInstance: [
      (value) => {
        if (!value) return "Data mesh instance is required";
      },
    ],
    algoReport: [
      (value) => {
        if (!value) return `${workflowType} is required`;
      },
    ],
  },
];

export type WorkflowDataSettings = {
  name: string;
  project: string;
  datameshInstance: string;
  algoReport: string;
  schedule: ScheduleInfo;
  region: string;
};

export const Settings: React.FC<{
  workflowType: string;
  errors: any;
  settingsData?: SettingsData;
  selectedSettings: WorkflowDataSettings;
  onChange: (key: string, value: string) => void;
}> = ({ workflowType, selectedSettings, settingsData, onChange, errors }) => {
  const { validateField } = useValidation();
  const [showScheduleSideBar, setShowScheduleSideBar] = useState(false);
  const getScheduleValue = () => {
    const daysOfWeekMapper = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const getDaysOfWeek = (daysOfWeek) => {
      return daysOfWeek
        .map((dayOfWeek) => {
          return daysOfWeekMapper[dayOfWeek];
        })
        .join(", ");
    };
    const getMonth = (month) => {
      if (month === "1") return `${month}st`;
      else if (month === "2") return `${month}nd`;
      else if (month === "3") return `${month}rd`;
      else return `${month}th`;
    };
    const { mvSchedule } = selectedSettings.schedule;
    if (!mvSchedule) return "N/A";
    const scheduleTitleCase =
      mvSchedule.charAt(0).toUpperCase() + mvSchedule.slice(1);
    const schedule = selectedSettings.schedule[mvSchedule];
    if (mvSchedule === "noSchedule") return "No Schedule";
    else if (schedule.cronExpression) {
      return `${scheduleTitleCase} ${schedule.cronExpression}`;
    } else if (mvSchedule === "weekly") {
      return `${scheduleTitleCase} on ${getDaysOfWeek(schedule.selectedDatesOfWeek)} at ${schedule.HR}:${schedule.MIN}`;
    } else if (mvSchedule === "monthly") {
      return `${scheduleTitleCase} on the ${getMonth(schedule.monthlyFreqDay)} at ${schedule.HR}:${schedule.MIN}`;
    } else return `${scheduleTitleCase} at ${schedule.HR}:${schedule.MIN}`;
  };
  return (
    <StepContainer withPadding>
      <ScheduleSidebar
        onSubmit={(data) => {
          setShowScheduleSideBar(false);
          onChange("schedule", data);
        }}
        initScheduleInfo={selectedSettings.schedule}
        onClose={() => setShowScheduleSideBar(false)}
        isOpen={showScheduleSideBar}
      />
      <BoxTitle>
        <BlackHighlight>Step 1:</BlackHighlight> Settings
      </BoxTitle>
      <HR />
      <BlueLabel>Workflow Details</BlueLabel>
      <RowContainer>
        <Name
          value={selectedSettings.name ?? ""}
          onChange={(e) => {
            validateField("name", e.target.value, validation(workflowType));
            onChange("name", e.target.value);
          }}
          error={errors["name"]}
        />
        <Project
          options={settingsData?.projects ?? []}
          value={selectedSettings.project ?? ""}
          onChange={(e) => {
            validateField("project", e.target.value, validation(workflowType));
            onChange("project", e.target.value);
          }}
          error={errors["project"]}
        />
        <div style={{ display: "flex" }}>
          <VR />
          <WorkflowScheduleContainer>
            <InputLabel>Workflow Schedule</InputLabel>
            <WorkflowSchedule
              value={getScheduleValue()}
              onClick={() => {
                setShowScheduleSideBar(true);
              }}
            />
          </WorkflowScheduleContainer>
        </div>
      </RowContainer>

      <HR />
      <BlueLabel>Service Configuration</BlueLabel>
      <RowContainer>
        <DataMeshInstance
          value={selectedSettings.datameshInstance ?? ""}
          options={settingsData?.instances ?? []}
          onChange={(e) => {
            validateField(
              "datameshInstance",
              e.target.value,
              validation(workflowType),
            );
            onChange("datameshInstance", e.target.value);
          }}
          error={errors["datameshInstance"]}
        />
        <AlgoReports
          options={settingsData?.services ?? []}
          error={errors["algoReport"]}
          workflowType={workflowType}
          value={selectedSettings.algoReport ?? ""}
          onChange={(e) => {
            validateField(
              "algoReport",
              e.target.value,
              validation(workflowType),
            );
            onChange("algoReport", e.target.value);
          }}
        />
        <Region
          error={errors["region"]}
          options={settingsData?.regions ?? []}
          value={selectedSettings.region ?? ""}
          onChange={(e) => {
            validateField("region", e.target.value, validation(workflowType));
            onChange("region", e.target.value);
          }}
        />
      </RowContainer>
    </StepContainer>
  );
};

const Region: React.FC<{
  value: string;
  error: any;
  options: { label: string; value: string; complianceDetails: string }[];
  onChange: (e: any) => void;
}> = ({ value, error, options, onChange }) => {
  const selectedRegion = options.find((option) => option.value === value);
  return (
    <FlexColumn>
      <InputLabel>Select Region</InputLabel>
      <TextField
        id={"regionSelect"}
        error={error}
        placeholder={"Region *"}
        selectOptions={options}
        type="select"
        helperText={error}
        dense
        value={value}
        onChange={onChange}
      ></TextField>
      <LabelContainer>
        <Label>Compliance Details:</Label>
        <Value isNA={!!selectedRegion?.complianceDetails}>
          {selectedRegion?.complianceDetails ?? "N/A"}
        </Value>
      </LabelContainer>
    </FlexColumn>
  );
};

const Name: React.FC<{
  value: string;
  error: any;
  onChange: (e: any) => void;
}> = ({ value, error, onChange }) => {
  return (
    <FlexColumn>
      <InputLabel>Name Workflow</InputLabel>
      <TextField
        dense={true}
        height="40"
        id={"nameTextField"}
        placeholder={"Workflow Name *"}
        value={value}
        required
        error={error}
        helperText={error}
        onChange={onChange}
      ></TextField>
    </FlexColumn>
  );
};

const Project: React.FC<{
  value: string;
  error: any;
  options: { label: string; value: string }[];
  onChange: (e: any) => void;
}> = ({ value, error, options, onChange }) => {
  return (
    <FlexColumn>
      <InputLabel>Choose Project</InputLabel>
      <TextField
        id={"projectSelect"}
        data-testid={"projectSelect"}
        placeholder={"Project *"}
        error={error}
        selectOptions={options}
        type="select"
        helperText={error}
        dense
        value={value}
        onChange={onChange}
      ></TextField>
    </FlexColumn>
  );
};

const DataMeshInstance: React.FC<{
  value: string;
  error: any;
  options: { label: string; value: string; region: string }[];
  onChange: (e: any) => void;
}> = ({ value, error, options, onChange }) => {
  const selectedInstance = options.find((option) => option.value === value);
  return (
    <FlexColumn>
      <InputLabel>Data Mesh Instance</InputLabel>
      <TextField
        id={"datameshSelect"}
        error={error}
        placeholder={"Data Mesh Instance *"}
        selectOptions={options}
        type="select"
        helperText={error}
        dense
        value={value}
        onChange={onChange}
      ></TextField>
      <LabelContainer>
        <Label>Region:</Label>
        <Value isNA={!!selectedInstance?.region}>
          {selectedInstance?.region ?? "N/A"}
        </Value>
        <Label>Instance ID:</Label>
        <Value isNA={!!selectedInstance?.region}>
          {selectedInstance?.value ?? "N/A"}
        </Value>
      </LabelContainer>
    </FlexColumn>
  );
};

const AlgoReports: React.FC<{
  workflowType: string;
  value: string;
  options: { label: string; value: string; region: string }[];
  error: any;
  onChange: (e) => void;
}> = ({ workflowType, value, error, options, onChange }) => {
  const selectedReport = options.find((option) => option.value === value);
  return (
    <FlexColumn>
      <InputLabel>{workflowType}</InputLabel>
      <TextField
        id={"algoReportSelect"}
        error={error}
        placeholder={"Choose Instance *"}
        selectOptions={options}
        type="select"
        helperText={error}
        dense
        value={value}
        onChange={onChange}
      ></TextField>
      <LabelContainer>
        <Label>Region:</Label>
        <Value isNA={!!selectedReport?.region}>
          {selectedReport?.region ?? "N/A"}
        </Value>
        <Label>Instance ID:</Label>
        <Value isNA={!!selectedReport?.region}>
          {selectedReport?.value ?? "N/A"}
        </Value>
      </LabelContainer>
    </FlexColumn>
  );
};

const WorkflowSchedule: React.FC<{ value: string; onClick: () => void }> = ({
  value,
  onClick,
}) => {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Label>Schedule:</Label>
        <Value>{value}</Value>
      </div>
      <Button
        width={"fit-content"}
        size={"small"}
        id={"editSchedule"}
        onClick={() => {
          onClick();
        }}
      >
        <AccessTimeIcon /> Add Schedule
      </Button>
    </>
  );
};
